import Vue from 'vue'
import api from '@/api'
import store from 'store'

const addOneSignalListeners = function (OneSignal) {
  if (OneSignal?.Notifications) {
    OneSignal.Notifications.addEventListener('permissionChange', async (permission) => {
      try {
        if (permission) {
          let data = store.get('ews_user', {})
          if (data?.profile?.empid) {
            const externalId = data?.profile?.empid.toString().toLowerCase()
            await OneSignal.login(externalId)
          }
          if (data?.profile?.email_address) {
            const email = data?.profile?.email_address.toString().toLowerCase()
            await OneSignal.User.addTag('email', email)
            await OneSignal.User.addEmail(email)
          }
        }
      } catch (error) {
        console.error('Error on OneSignal notification permissionChange', error)
      }
    })
  }
  if (OneSignal?.User?.PushSubscription) {
    OneSignal.User.PushSubscription.addEventListener('change', async (event) => {
      try {
        if (event.current.token && event.current.id) {
          let data = store.get('ews_user', {})
          if (data?.profile?.empid) {
            const externalId = data?.profile?.empid.toString().toLowerCase()
            await OneSignal.login(externalId)
          }
          if (data?.profile?.email_address) {
            const email = data?.profile?.email_address.toString().toLowerCase()
            await OneSignal.User.addTag('email', email)
            await OneSignal.User.addEmail(email)
          }
        }
      } catch (error) {
        console.error('Error on OneSignal subscription change', error)
      }
    })
  }
}

export default {
  state: {
    onesignalLogin: false,
    onesignalInit: false,
    isOneSignalInitializing: false
  },
  mutations: {
    SET_ONESIGNAL_LOGIN(state) {
      state.onesignalLogin = true
    },
    SET_ONESIGNAL_INIT(state) {
      state.onesignalInit = true
      state.isOneSignalInitializing = false
    },
    SET_ONESIGNAL_INITIALIZING(state) {
      state.isOneSignalInitializing = true
    },
    UNSET_ONESIGNAL_INITIALIZING(state) {
      state.isOneSignalInitializing = false
    }
  },
  actions: {
    async oneSignalInit({ state, commit, rootState }) {
      return new Promise((resolve, reject) => {
        // Don't initialize for tokenized users
        if (rootState.isTokenizedAccess) return

        console.debug('INITIALIZING ONE SIGNAL')
        if (state.onesignalInit) {
          console.debug('OneSignal already initialized')
          return
        }
        if (state.isOneSignalInitializing) {
          console.debug('OneSignal already initializing')
          return
        }
        const currentStation = rootState?.station?.current?.station || ''
        const notificationEnabledStationsList = rootState?.station?.notificationEnabledStationsList || []

        if (!notificationEnabledStationsList.includes(currentStation)) {
          return
        }
        commit('SET_ONESIGNAL_INITIALIZING')

        // hardcoded app id for now
        const oneSignalAppId = import.meta.env.VITE_ONESIGNAL_APP_ID
        const isLocal = import.meta.env.VITE_ENV === 'local'

        if (oneSignalAppId && !state.onesignalInit) {
          window.OneSignalDeferred = window.OneSignalDeferred || []
          OneSignalDeferred.push(async function (OneSignal) {
            // turn off logging
            OneSignal.Debug.setLogLevel('none')
            try {
              let initOptions = {
                appId: oneSignalAppId,
                allowLocalhostAsSecureOrigin: isLocal,
                notifyButton: {
                  enable: true
                },
                serviceWorkerParam: { scope: '/onesignal_sw/' },
                serviceWorkerPath: 'OneSignalSDKWorker.js'
              }
              if (isLocal) initOptions.safari_web_id = import.meta.env.VITE_ONESIGNAL_APP_SAFARI_WEB_ID

              await OneSignal.init(initOptions)
              commit('SET_ONESIGNAL_INIT')
              try {
                addOneSignalListeners(OneSignal)
              } catch (error) {
                console.warn('addOneSignalListeners error', error)
              }
            } catch (error) {
              console.warn('Error initializing OneSignal', error)
              commit('UNSET_ONESIGNAL_INITIALIZING')
              reject(error)
              return
            }
            resolve('OneSignal initialized')
          })
        }
      })
    },

    async oneSignalLogin({ state, commit, dispatch, rootState }) {
      console.debug('ONE SIGNAL LOGIN')
      // sleep for 1 second to wait for OneSignal to initialize
      await new Promise((resolve) => setTimeout(resolve, 1000))

      if (!state.onesignalInit) {
        console.warn('OneSignal not initialized')
        return
      }
      if (rootState.isTokenizedAccess) {
        return
      }
      if (state.onesignalLogin) {
        console.warn('OneSignal already logged in')
        return
      }
      const currentStation = rootState?.station?.current?.station || ''
      const notificationEnabledStationsList = rootState?.station?.notificationEnabledStationsList || []
      if (!notificationEnabledStationsList.includes(currentStation)) {
        return
      }
      if (!state.onesignalLogin) {
        return new Promise((resolve, reject) => {
          window.OneSignalDeferred = window.OneSignalDeferred || []
          OneSignalDeferred.push(async function (OneSignal) {
            try {
              let data = store.get('ews_user', {})
              if (data?.profile?.empid) {
                const externalId = data?.profile?.empid.toString().toLowerCase()
                await OneSignal.login(externalId)
                // how do we know if the externalId exists in oneSignal? handle that here
                // const result = await api.oneSignal.checkUserByExternalId(externalId)
                // console.log('OneSignal login response', OneSignal, result)

                await dispatch('oneSignalSaveSubscription')
                if (data?.profile?.email_address) {
                  const email = data?.profile?.email_address.toString().toLowerCase()
                  await OneSignal.User.addTag('email', email)
                  await OneSignal.User.addEmail(email)
                }
              }
              resolve('OneSignal login')
            } catch (error) {
              console.warn('Error oneSignalLogin: ', error)
              reject(error)
            }
          })
          commit('SET_ONESIGNAL_LOGIN')
        })
      }
    },
    oneSignalLogout({ rootState }) {
      return new Promise((resolve, reject) => {
        // hardcoded app id for now
        window.OneSignalDeferred = window.OneSignalDeferred || []
        OneSignalDeferred.push(async function (OneSignal) {
          try {
            await OneSignal.User.removeTag('email')
            await OneSignal.logout()
            resolve('OneSignal logout')
          } catch (error) {
            reject(error)
          }
        })
      })
    },
    async oneSignalSaveSubscription() {
      try {
        if (Vue.prototype.$OneSignal) {
          const subscriptionData = Vue.prototype.$OneSignal.User.PushSubscription || {}
          const userData = store.get('ews_user', {})
          let empid
          if (userData?.profile?.empid) {
            empid = userData?.profile?.empid.toString().toLowerCase()
          }

          if (!empid) {
            throw new Error('could not get empid from store')
          }

          if (!subscriptionData.id) {
            throw new Error('subscriptionData.id is empty')
          }
          const data = {
            subscriptionId: subscriptionData.id,
            subscriptionData: subscriptionData,
            empid: empid,
            domain: window.location.origin || '',
            deviceData: {
              userAgent: window.navigator.userAgent
            }
          }
          await api.newsdesk.saveOneSignalNotificationSubscription(data)
        }
      } catch (error) {
        console.warn('Error oneSignalSaveSubscription: ', error)
      }
    }
  },
  getters: {
    isOneSignalInitialized(state) {
      return state.onesignalInit
    }
  }
}
